// src/context/AuthContext.tsx
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Session, User } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";
import { supabase } from "@/lib/supabase-client";
import { useAnalyticsIdentity } from "@/hooks/useAnalyticsIdentity";
import { isSuperAdmin } from "@/lib/super-admin";
import { fetchMyPlayerAccount, type PlayerAccount } from "@/lib/services/player-accounts";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  /** undefined = still fetching; null = no player_accounts row; PlayerAccount = loaded */
  playerAccount: PlayerAccount | null | undefined;
  refetchPlayerAccount: () => Promise<void>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, captchaToken?: string, emailRedirectTo?: string) => Promise<{ error: string | null }>;
  signInWithGoogle: (redirectTo?: string) => Promise<{ error: string | null }>;
  resetPassword: (email: string, redirectTo: string, captchaToken?: string) => Promise<{ error: string | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
  resendConfirmation: (email: string, emailRedirectTo?: string, captchaToken?: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // undefined = not yet fetched; null = fetched, no row; PlayerAccount = fetched, has row
  const [playerAccount, setPlayerAccount] = useState<PlayerAccount | null | undefined>(undefined);
  // Which auth user the value above was fetched for. Without this, a value left
  // over from a previous user (notably the `null` set while signed out) reads as
  // "loaded, no account" for the new one.
  const [accountUserId, setAccountUserId] = useState<string | null>(null);

  const loadPlayerAccount = useCallback(
    async (userId: string | undefined, isCancelled: () => boolean = () => false) => {
      if (!userId) {
        setPlayerAccount(null);
        setAccountUserId(null);
        return;
      }
      try {
        const account = await fetchMyPlayerAccount();
        if (isCancelled()) return;
        setPlayerAccount(account);
        setAccountUserId(userId);
      } catch {
        if (isCancelled()) return;
        setPlayerAccount(null);
        setAccountUserId(userId);
      }
    },
    []
  );

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setIsLoading(false);
      Sentry.setUser(newSession ? { id: newSession.user.id } : null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Kept out of onAuthStateChange so the fetch isn't issued from inside
  // supabase-js's auth callback.
  const userId = session?.user?.id;

  // League caches (my communities, admin roles, seeded organizer data) aren't keyed by user, so a
  // different user signing in -- or signing out -- on the same browser must not inherit them.
  const queryClient = useQueryClient();
  const lastUserId = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (isLoading) return;
    const current = userId ?? null;
    if (lastUserId.current !== undefined && lastUserId.current !== current) {
      queryClient.resetQueries({ queryKey: ["league"] });
      // ladder_me and my-fixtures are per user too (spec §3).
      queryClient.resetQueries({ queryKey: ["ladder"] });
    }
    lastUserId.current = current;
  }, [userId, isLoading, queryClient]);
  useEffect(() => {
    if (isLoading) return; // no auth event yet — nothing to load for
    let cancelled = false;
    loadPlayerAccount(userId, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [userId, isLoading, loadPlayerAccount]);

  // Resolved at render time, not in an effect. React flushes child effects
  // before the provider's, so a consumer would otherwise observe the previous
  // user's value for one commit — Login's one-shot redirect latched on exactly
  // that and sent already-claimed players to /claim.
  const playerAccountForUser = !userId
    ? null
    : accountUserId === userId
      ? playerAccount
      : undefined; // signed in, account not fetched for this user yet

  // Uses the id already in state rather than re-asking the auth server. A
  // getUser() network blip resolves to { user: null } instead of throwing, which
  // would clear accountUserId while a session is still active — leaving the
  // account permanently "loading" (nothing re-triggers the effect above, whose
  // deps are unchanged) and every gated page stuck on a spinner until reload.
  const refetchPlayerAccount = useCallback(async () => {
    await loadPlayerAccount(userId);
  }, [loadPlayerAccount, userId]);

  const signIn = useCallback(async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password, options: { captchaToken } });
    return { error: error?.message ?? null };
  }, []);

  const signUp = useCallback(async (email: string, password: string, captchaToken?: string, emailRedirectTo?: string) => {
    const { error } = await supabase.auth.signUp({
      email, password,
      options: { captchaToken, emailRedirectTo: emailRedirectTo ?? `${window.location.origin}/auth/callback` },
    });
    return { error: error?.message ?? null };
  }, []);

  const signInWithGoogle = useCallback(async (redirectTo?: string) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      // select_account: without it Google silently reuses the device's only signed-in account, which on a
      // shared phone (partner's iPhone, family iPad) signs the wrong person into ScoreGlass.
      options: { redirectTo: redirectTo ?? `${window.location.origin}/auth/callback`, queryParams: { prompt: "select_account" } },
    });
    return { error: error?.message ?? null };
  }, []);

  const resetPassword = useCallback(async (email: string, redirectTo: string, captchaToken?: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo, captchaToken });
    return { error: error?.message ?? null };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error: error?.message ?? null };
  }, []);

  const resendConfirmation = useCallback(async (email: string, emailRedirectTo?: string, captchaToken?: string) => {
    const { error } = await supabase.auth.resend({
      type: "signup", email,
      options: { emailRedirectTo: emailRedirectTo ?? `${window.location.origin}/auth/callback`, captchaToken },
    });
    return { error: error?.message ?? null };
  }, []);

  // PostHog gets the internal auth user id only. playerAccountForUser is undefined while it loads,
  // so the hook waits for it rather than tagging a player as "signed_in" first.
  useAnalyticsIdentity({
    isLoading,
    userId,
    userType: playerAccountForUser === undefined ? undefined : playerAccountForUser ? "player" : "signed_in",
    isSuperAdmin: isSuperAdmin(session?.user?.email),
  });

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setPlayerAccount(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: session?.user ?? null,
        session,
        isLoading,
        playerAccount: playerAccountForUser,
        refetchPlayerAccount,
        signIn,
        signUp,
        signInWithGoogle,
        resetPassword,
        updatePassword,
        resendConfirmation,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
